import { ArrowGo } from "@assets"
import { Head, Link, router } from "@inertiajs/react"

import { ContentActionsMenu } from "@/components/content-actions-menu"
import {
  Stories,
  StoriesContent,
  Story,
  StoryImage,
  StoryVideo,
} from "@/components/kibo-ui/stories"
import { PageHeader } from "@/components/page-header"
import AppLayout from "@/layouts/app-layout"
import { announcementsPath } from "@/routes"

interface Image {
  id: number | string
  url: string
}
interface Video {
  id: number | string
  url: string
}

interface Announcement {
  id: number | string
  content: string
  created_at: string
  status?: string
  author?: { id: number; name: string; avatar_url?: string; verified?: boolean }
  neighborhood?: { id: number; name: string } | null
  images?: Image[]
  video_url?: string | null
  videos?: Video[]
  cover_url?: string | null
  shares_count?: number
}

interface Props {
  announcement: Announcement
  can_edit?: boolean
}

export default function AnnouncementShow({ announcement, can_edit }: Props) {
  const handleDelete = () => {
    if (!confirm("¿Eliminar este anuncio? Esta acción no se puede deshacer."))
      return
    router.delete(`/anuncios/${announcement.id}`, {
      preserveScroll: false,
      onSuccess: () => {
        router.visit("/feed")
      },
    })
  }

  const hasImages =
    (announcement.images?.length ?? 0) > 0 || !!announcement.cover_url
  const hasVideo =
    (announcement.videos && announcement.videos.length > 0) ??
    !!announcement.video_url

  const videoList = (announcement.videos ?? []).map((v) => ({
    type: "video" as const,
    src: v.url,
    id: v.id,
  }))
  if (videoList.length === 0 && announcement.video_url) {
    videoList.push({
      type: "video" as const,
      src: String(announcement.video_url),
      id: "main-video",
    })
  }
  const imageList = (announcement.images ?? []).map((img) => ({
    type: "image" as const,
    src: img.url,
    id: img.id,
  }))
  if (imageList.length === 0 && announcement.cover_url) {
    imageList.push({
      type: "image" as const,
      src: String(announcement.cover_url),
      id: "cover",
    })
  }
  const allMedia = [...videoList, ...imageList]

  return (
    <AppLayout
      showBackButton={false}
      showEditButton={can_edit}
      showDeleteButton={can_edit}
      onEdit={() => router.visit(`/anuncios/${announcement.id}/edit`)}
      onDelete={handleDelete}
    >
      <Head title="Anuncio" />

      <div className="mx-auto max-w-6xl space-y-4 px-4 py-4 md:space-y-6 md:p-6">
        {/* Desktop header actions */}
        <div className="hidden md:block">
          <PageHeader
            showBackButton={false}
            showEdit={can_edit}
            showDelete={can_edit}
            editHref={`/anuncios/${announcement.id}/edit`}
            onDelete={handleDelete}
          />
        </div>
        <div className="flex items-center justify-between">
          <div className="text-primary flex flex-wrap items-center gap-2 text-[13px] font-medium tracking-[0.13px]">
            <Link href={announcementsPath()}>
              <div className="flex items-center gap-1 pt-[3px]">
                <ArrowGo className="text-primary size-4 rotate-180 fill-current" />
                Anuncios
              </div>
            </Link>
          </div>
          <ContentActionsMenu
            contentType="Announcement"
            contentId={Number(announcement.id)}
            authorId={announcement.author?.id ?? 0}
            authorName={announcement.author?.name ?? "Usuario"}
          />
        </div>

        <div
          className="prose dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: announcement.content }}
        />

        {/* Main content */}
        <div className="space-y-6">
          {(hasVideo || hasImages) && allMedia.length > 0 && (
            <div className="w-full">
              <Stories
                className="w-full"
                opts={{
                  align: "start",
                  dragFree: true,
                  containScroll: "trimSnaps",
                }}
              >
                <StoriesContent className="-ml-0 flex gap-4 pe-6">
                  {allMedia.map((media) => (
                    <Story
                      key={media.id}
                      className="min-w-0 flex-shrink-0 basis-auto pl-0"
                    >
                      <div className="aspect-[3/4] w-44 cursor-pointer overflow-hidden rounded-xl bg-black/5 shadow-sm transition-transform hover:scale-[1.02] sm:w-52 md:w-60">
                        {media.type === "video" ? (
                          <StoryVideo src={media.src} />
                        ) : (
                          <StoryImage src={media.src} alt="Story" />
                        )}
                      </div>
                    </Story>
                  ))}
                </StoriesContent>
              </Stories>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
